import {
  email as emailRegexStr,
  password as passwordRegexStr
} from '../../util/regex'
import { UUIDSchema } from '../../util/schema'

/**
 * A module containing schema validation and documentation related to Users
 * @module ducks/user/schemas
 */

/** @typedef {('CREATED'|'CREATING'|error)} CreateEnum */
/** @typedef {('UPDATED'|'UPDATING'|error)} UpdateEnum */
/** @typedef {('LOGGEDIN'|'LOGGINGIN'|error)} LoginEnum */

/** A JSONSchema definition for a user login request */
/* eslint-disable camelcase */
export const LoginRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    username: {
      type: 'string',
      minimum: 1
    },
    password: {
      type: 'string',
      minimum: 1
    },
    client_id: {
      type: 'string',
      minimum: 1
    },
    client_secret: {
      type: 'string',
      minimum: 1
    },
    grant_type: {
      type: 'string',
      enum: ['password']
    }
  },
  required: ['username', 'password', 'client_id', 'client_secret', 'grant_type']
}
/* eslint-enable camelcase */

/** Schema for a user create request */
export const UserCreateRequestSchema = {
  /* eslint-disable camelcase */
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    first_name: {
      type: 'string',
      minimum: 1
    },
    last_name: {
      type: 'string',
      minimum: 1
    },
    email_address: {
      type: 'string',
      pattern: emailRegexStr
    },
    password: {
      type: 'string',
      pattern: passwordRegexStr
    },
    phone_number: {
      type: 'string',
      minimum: 1
    },
    preference: {
      type: 'array',
      items: PreferenceSchema
    },
    pro: {
      type: 'boolean'
    },
    photo_bytes: {
      type: ['string', 'null']
    },
    username: {
      type: 'string'
    }
  },
  additionalProperties: false,
  required: [
    'first_name',
    'last_name',
    'email_address',
    'password',
    'preference'
  ]
  /* eslint-enable camelcase */
}

/** Schema for a user update request */
export const UserUpdateRequestSchema = {
  /* eslint-disable camelcase */
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    user_id: UUIDSchema,
    first_name: {
      type: 'string',
      minimum: 1
    },
    last_name: {
      type: 'string',
      minimum: 1
    },
    email_address: {
      type: 'string',
      pattern: emailRegexStr
    },
    phone_number: {
      type: 'string',
      minimum: 1
    },
    photo_bytes: {
      type: 'string'
    },
    pro: {
      type: 'boolean'
    }
  },
  additionalProperties: false,
  required: ['user_id']
  /* eslint-enable camelcase */
}

/** Schema for a user preference */
export const PreferenceSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minimum: 1
    },
    value: {
      type: 'any',
      minimum: 1
    }
  },
  required: ['name', 'value']
}

/** Schema for a user role */
export const RoleSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'string',
  enum: ['OWNER', 'MANAGER', 'QUICK_RUN']
}

/**
 * @typedef {Object} Preference
 * @property {string} type - How the value of the property should be treated;
 *                           one of ['UNIT', 'NUMBER', 'BOOLEAN']
 * @property {string} name - Preference id; a dot seperated string of namespaces
 * @property {string|number|Boolean} value - Value of the preference
 * @see PreferenceSchema
 */

/**
 * @typedef {Object} User
 * @property {string} firstName - User's first name
 * @property {string} lastName - User's last name
 * @property {string} emailAddress - User's email address
 * @property {Preference[]} preferences - Array of user preferences
 * @property {string} username - User's username
 */
